namespace Mos {
  export class AppMetadata extends OSF.Manifest.Manifest {
    _acquisition: Mos.Acquisition;
    _actualPayload: OSF.PayloadType;
    _hostType: string;
    _hostInfo: OfficeExt.IHostContextInfo;

    static fromString(
      payload: string,
      payloadType: OSF.PayloadType,
      locale: string,
      solutionRef?: OfficeExt.OsfSolutionReference,
    ): AppMetadata {
      let acquisition: Mos.Acquisition = new Mos.Acquisition();
      acquisition.init(JSON.parse(payload));
      return new this(acquisition, payloadType, locale, solutionRef);
    }

    static fromAcquisition(
      acquisition: Mos.Acquisition,
      locale: string,
      solutionRef?: OfficeExt.OsfSolutionReference,
    ): AppMetadata {
      return new this(acquisition, OSF.PayloadType.MosAcquisition, locale, solutionRef);
    }

    constructor(
      acquisition: Mos.Acquisition,
      payloadType: OSF.PayloadType,
      locale: string,
      solutionRef?: OfficeExt.OsfSolutionReference,
    ) {
      if (acquisition.titleDefinition.elementDefinitions.officeAddIns?.length > 0) {
        // If the JSON payload contains a UTF-16 BOM character it will fail the JSON.parse as well as XML parsing. We should remove the BOM
        let xmlPayload: string = acquisition.titleDefinition.elementDefinitions.officeAddIns[0].xmlDefinition;

        let codepointLength = OSF.OUtil.getBOMCodepointLength(xmlPayload);
        if (codepointLength > 0) {
          xmlPayload = xmlPayload.substring(codepointLength);
        }

        super(xmlPayload, locale, OSF.PayloadType.OfficeAddin, solutionRef); // Payload type passed to Osf.Manifest is OfficeAddin
        this._acquisition = acquisition;
      } else {
        super("", locale, OSF.PayloadType.MosAcquisition, solutionRef);
        this._acquisition = acquisition;

        this._hostInfo = null;
        this._hostInfo = OfficeExt.CatalogFactory.getHostContext();
        if (this._hostInfo != null) {
          this._hostType = OSF.getManifestHostType(this._hostInfo.hostType);
        }
        // If the Acquisition payload contains extension
        if (acquisition.titleDefinition.elementDefinitions.extensions?.length > 0) {
          this._populateManifest();
        }
      }

      this._actualPayload = payloadType;
    }

    private _populateManifest() {
      this._initPopulator();
      this._populateBaseProperties();
      this._populateExtensionSettings();
      this._populateAddinCommands();
      this._populateWebApplicationInfo();
    }

    private _initPopulator() {
      // initialize members
      this._displayNames = {};
      this._descriptions = {};
      this._iconUrls = {};
      this._extensionSettings = {};
      this._highResolutionIconUrls = {};
      this._ribbonExtensionControls = {};
      this._versionOverrides = {};
      this._supportUrls = {};
      this._versionOverrides[this._hostType] = {};
    }

    private _populateBaseProperties() {
      this._manifestSchemaVersion = "1.1";
      this._id = this._acquisition.manifestId;
      this._providerName = this._acquisition.titleDefinition.developerName;
      this._defaultLocale = this._UILocale;

      this._populateBaseAllowSnapshot();
      this._idIssuer = OSF.ManifestIdIssuer.Custom;

      this._addSupportUrl(this._UILocale, this._acquisition.titleDefinition.developerUrl);
      this._addDisplayName(this._UILocale, this._acquisition.titleDefinition.name);
      this._addDescription(this._UILocale, this._acquisition.titleDefinition.shortDescription);
      this._addIconUrl(this._UILocale, this._acquisition.titleDefinition.iconSmall?.uri);

      this._populateBaseOfficeAppType();
      this._populateBaseVersion();
      this._populateBaseDomains();
      this._populateBasePermissions();
      this._populateRuntimes();

      this._populateBaseHosts();
      this._populateBaseEquivalentAddins();
      this._populateBaseHighResolutionIconUrls();
    }

    private _populateBaseOfficeAppType() {
      // For WXP only Taskpane and Content App is supported.
      // Content App has contentRuntimes schema.
      if (this._acquisition.titleDefinition.elementDefinitions.extensions[0].contentRuntimes?.length > 0) {
        this._target = OSF.OfficeAppType.ContentApp;
      } else {
        this._target = OSF.OfficeAppType.TaskPaneApp;
      }
    }

    private _populateBaseVersion() {
      try {
        this._version = this.complementVersion(this._acquisition.titleDefinition.version);
      } catch {
        this._version = this._acquisition.titleDefinition.version;
      }
    }

    private _populateBaseHosts() {
      if (this._acquisition.titleDefinition.elementDefinitions.extensions[0].requirements?.scopes?.length > 0) {
        const hosts = this._acquisition.titleDefinition.elementDefinitions.extensions[0].requirements.scopes
          .map(this.getHost)
          .filter((host) => !!host);

        if (hosts.length > 0) {
          this._hosts = hosts;

          const hostStrings = hosts.map((host) => `<Host Name="${host}"/>`).join("");
          this._hostsXml = `<Hosts ${OSF.ManifestNamespaces["1.1"]}>${hostStrings}</Hosts>`;
        }
      }
    }

    private getHost(host: string) {
      switch (host?.toLowerCase()) {
        case "workbook":
          return OSF.ManifestHostType.Workbook;
        case "document":
          return OSF.ManifestHostType.Document;
        case "presentation":
          return OSF.ManifestHostType.Presentation;
      }

      return null;
    }

    private _populateBasePermissions() {
      this._permissions = OSF.OsfControlPermission.Restricted;
      this.getControlPermissionsFromMosPermissions();
    }

    private getControlPermissionsFromMosPermissions() {
      let permissions: Mos.Permissions = this.getExtensionPermissions();
      if (permissions != null) {
        for (let i: number = 0; i < permissions.resourceSpecific.length; i++) {
          const resourceSpecific: Mos.ResourceSpecific = permissions.resourceSpecific[i];
          if (resourceSpecific.type === Mos.ResourceSpecificType.Delegated) {
            switch (resourceSpecific.name) {
              case "Document.Restricted.User":
                if (this._permissions < OSF.OsfControlPermission.Restricted) {
                  this._permissions = OSF.OsfControlPermission.Restricted;
                }
                break;
              case "Document.Read.User":
                if (this._permissions < OSF.OsfControlPermission.ReadDocument) {
                  this._permissions = OSF.OsfControlPermission.ReadDocument;
                }
                break;
              case "Document.ReadAll.User":
                if (this._permissions < OSF.OsfControlPermission.ReadAllDocument) {
                  this._permissions = OSF.OsfControlPermission.ReadAllDocument;
                }
                break;
              case "Document.Write.User":
                if (this._permissions < OSF.OsfControlPermission.WriteDocument) {
                  this._permissions = OSF.OsfControlPermission.WriteDocument;
                }
                break;
              case "Document.ReadWrite.User":
                if (this._permissions < OSF.OsfControlPermission.ReadWriteDocument) {
                  this._permissions = OSF.OsfControlPermission.ReadWriteDocument;
                }
                break;
            }
          }
        }
      }
    }

    private _populateBaseDomains() {
      this._appDomains = []; //TODO
    }

    // override base method
    private _populateExtensionSettings() {
      let settings;
      let sourceLocation;
      let requestedHeight;
      let requestedWidth;
      if (this._acquisition.titleDefinition.elementDefinitions.extensions[0].contentRuntimes?.length > 0) {
        const contentRuntime = this._acquisition.titleDefinition.elementDefinitions.extensions[0].contentRuntimes[0];
        sourceLocation = contentRuntime.code.page;
        requestedHeight = contentRuntime.requestedHeight;
        requestedWidth = contentRuntime.requestedWidth;
      } else if (this._acquisition.titleDefinition.elementDefinitions.extensions[0].runtimes?.length > 0) {
        const runtime = this._acquisition.titleDefinition.elementDefinitions.extensions[0].runtimes[0];
        sourceLocation = runtime.code.page;
      }

      if (!!sourceLocation) {
        for (let formFactor in OSF.FormFactor) {
          settings = new OSF.Manifest.ExtensionSettings();
          settings._addSourceLocation(this._UILocale, sourceLocation);
          this.addManifestUrl(sourceLocation);

          if (!!requestedHeight && !!requestedHeight) {
            settings._setDefaultHeight(requestedHeight);
            settings._setDefaultWidth(requestedWidth);
          }
          this._extensionSettings[formFactor] = settings;
        }
      }
      if (!settings) {
        //no setting found
        throw OsfMsAjaxFactory.msAjaxError.argument("Manifest");
      }
    }

    private _populateAddinCommands() {
      // Populate add-in commands and cache it.
      let addinCommandsParser: OfficeExt.Parser.AddinCommandsManifestParser =
        new OfficeExt.Parser.AddinCommandsManifestParser(this._hostType, "DesktopFormFactor");
      let errorManager = {
        setErrorMessageForAddin: (errorMessage) => {
          OsfMsAjaxFactory.msAjaxDebug.trace("Error parsing app manifest: " + errorMessage);
        },
      };

      let entitlement: OfficeExt.AddinEntitlement = this._getEntitlement(this._solutionRef);

      // TODO: customFunction using customFunctionParser

      let addinCommands = addinCommandsParser.parseExtensions(
        entitlement,
        this as any,
        errorManager,
      ) as OfficeExt.Parser.VersionOverrides;
      this._versionOverrides[this._hostType]._addinCommandsExtensionPoints = addinCommands.ExtensionPoints;
      this._versionOverrides[this._hostType]._functionFileResid = addinCommands.functionFileResid;
      this._versionOverrides[this._hostType]._showTaskpaneControls = this._retrieveAppCommandShowTaskpaneControls(
        this._hostType,
      );
      // validate if taskpaneid and runtime resid pair of show taskpane control is valid or not.
      this._validateShowTaskpaneSharedRuntime(this._hostType);
      this._generateRibbonExtensionControlMap(this._hostType);
    }

    private _populateWebApplicationInfo() {
      const webApplicationInfo: WebApplicationInfo = this.getWebApplicationInfo();
      if (webApplicationInfo != null) {
        this._webApplicationResource = webApplicationInfo.resource;
        this._webApplicationId = webApplicationInfo.id;
      }
    }

    private _populateRuntimes() {
      let mosRuntimes: Mos.Runtimes[] = this.getExtensionRuntimes();
      let runtimes: OSF.Manifest.Runtimes = new OSF.Manifest.Runtimes();
      mosRuntimes.forEach(function (mosRuntime: Mos.Runtimes) {
        let runtime = new OSF.Manifest.Runtime();
        runtime.Resid = mosRuntime.id;
        runtime.LifeTime = mosRuntime.lifetime === "long" ? RuntimeLifeTime.Long : RuntimeLifeTime.Short;
        // We need to only add this runtime if the lifetime is long(SharedRuntime) as on WAC, a runtime in the runtimes list will be considered as a shared runtime
        // this behavior is different on Win32.
        if (runtime.LifeTime === RuntimeLifeTime.Long) {
          runtimes.add(runtime);
        }
      });

      this._versionOverrides[this._hostType]._runtimes = runtimes;
    }

    private _populateBaseAllowSnapshot() {
      this._allowSnapshot = true;
      const contentRuntimes = this._acquisition.titleDefinition.elementDefinitions.extensions[0].contentRuntimes;
      if (contentRuntimes?.length > 0) {
        this._allowSnapshot = !contentRuntimes[0].disableSnapshot;
      }
    }

    private _populateBaseHighResolutionIconUrls() {
      const alternates = this.getExtensionAlternates();
      if (alternates.length > 0 && alternates[0].alternateIcons?.highResolutionIcon?.url) {
        this._addHighResolutionIconUrl(this._UILocale, alternates[0].alternateIcons.highResolutionIcon.url);
      }
    }

    private _populateBaseEquivalentAddins() {
      const alternates = this.getExtensionAlternates();
      if (alternates.length > 0) {
        let equivalentAddins = new OSF.Manifest.EquivalentAddins();

        alternates.forEach((alternate) => {
          if (alternate?.prefer?.comAddin?.progId) {
            const equivalentAddin = new OSF.Manifest.EquivalentAddin();
            equivalentAddin._progId = alternate.prefer.comAddin.progId;
            equivalentAddin._type = "COM";

            equivalentAddins.add(equivalentAddin);
          }
        });

        this._equivalentAddins = equivalentAddins;
      }
    }

    public containsAddinCommands(manifestHostType): boolean {
      if (this.containsExtension()) {
        return (
          this.getExtensionShortcut() != null ||
          this.getExtensionRibbon() != null ||
          this.getExtensionContextMenu() != null ||
          this.getAddinCommandRuntimes().length > 0
        );
      } else if (this.containsOfficeAddin()) {
        return super.containsAddinCommands(manifestHostType);
      }
      return false;
    }

    public containsCustomFunctions(manifestHostType): boolean {
      return this.containsOfficeAddin() && super.containsCustomFunctions(manifestHostType); //TODO for Mos apps
    }

    public getAcquisition(): Mos.Acquisition {
      return this._acquisition;
    }

    // AppMetadata getters
    public getExtensionRuntimes(): Mos.Runtimes[] {
      let runtimes: Mos.Runtimes[] = [];
      if (
        this.containsExtension() &&
        this._acquisition.titleDefinition.elementDefinitions.extensions[0].runtimes != null
      ) {
        for (const runtime of this._acquisition.titleDefinition.elementDefinitions.extensions[0].runtimes) {
          if (Mos.AppMetadata.checkRequirements(runtime.requirements, "getExtensionRuntimes")) {
            runtimes.push(runtime);
          }
        }
      }

      return runtimes;
    }

    public getLegacyExtensionRuntime(): Mos.Runtimes {
      let legacyRuntime: Mos.Runtimes;
      if (this.containsExtension()) {
        for (const runtime of this.getExtensionRuntimes()) {
          // if no actual legacy runtime is found then return the first runtime as the legacy runtime.
          legacyRuntime = runtime;
          if (Mos.AppMetadata.isLegacyRuntime(runtime)) {
            return runtime;
          }
        }
      }

      return legacyRuntime;
    }

    public getAddinCommandRuntimes(): Mos.Runtimes[] {
      let runtimes: Mos.Runtimes[] = [];
      if (
        this.containsExtension() &&
        this._acquisition.titleDefinition.elementDefinitions.extensions[0].runtimes != null
      ) {
        for (const runtime of this._acquisition.titleDefinition.elementDefinitions.extensions[0].runtimes) {
          if (
            Mos.AppMetadata.checkRequirements(runtime.requirements, "getAddinCommandRuntimes") &&
            !Mos.AppMetadata.isLegacyRuntime(runtime)
          ) {
            runtimes.push(runtime);
          }
        }
      }

      return runtimes;
    }

    public getExtensionGetStartedMessage(): Mos.GetStartedMessages {
      if (
        this.containsExtension() &&
        this._acquisition.titleDefinition.elementDefinitions.extensions[0].getStartedMessages != null
      ) {
        for (const getStartedMessage of this._acquisition.titleDefinition.elementDefinitions.extensions[0]
          .getStartedMessages) {
          if (Mos.AppMetadata.checkRequirements(getStartedMessage.requirements, "getExtensionGetStartedMessage")) {
            return getStartedMessage;
          }
        }
      }

      return null;
    }

    public getExtensionShortcut(): Mos.Shortcuts {
      if (
        this.containsExtension() &&
        this._acquisition.titleDefinition.elementDefinitions.extensions[0].shortcuts != null
      ) {
        for (const shortcut of this._acquisition.titleDefinition.elementDefinitions.extensions[0].shortcuts) {
          if (Mos.AppMetadata.checkRequirements(shortcut.requirements, "getExtensionShortcut")) {
            return shortcut;
          }
        }
      }

      return null;
    }

    public getExtensionRibbon(): Mos.Ribbons {
      if (
        this.containsExtension() &&
        this._acquisition.titleDefinition.elementDefinitions.extensions[0].ribbons != null
      ) {
        for (const ribbon of this._acquisition.titleDefinition.elementDefinitions.extensions[0].ribbons) {
          if (Mos.AppMetadata.checkRequirements(ribbon.requirements, "getExtensionRibbon")) {
            return ribbon;
          }
        }
      }

      return null;
    }

    public getExtensionContextMenu(): Mos.ContextMenus {
      if (
        this.containsExtension() &&
        this._acquisition.titleDefinition.elementDefinitions.extensions[0].contextMenus != null
      ) {
        for (const contextMenu of this._acquisition.titleDefinition.elementDefinitions.extensions[0].contextMenus) {
          if (Mos.AppMetadata.checkRequirements(contextMenu.requirements, "getExtensionContextMenu")) {
            return contextMenu;
          }
        }
      }

      return null;
    }

    public getExtensionAlternates(): Mos.Alternates[] {
      let alternates: Mos.Alternates[] = [];
      if (
        this.containsExtension() &&
        this._acquisition.titleDefinition.elementDefinitions.extensions[0].alternates != null
      ) {
        for (const alternate of this._acquisition.titleDefinition.elementDefinitions.extensions[0].alternates) {
          if (Mos.AppMetadata.checkRequirements(alternate.requirements, "getExtensionAlternates")) {
            alternates.push(alternate);
          }
        }
      }

      return alternates;
    }

    public getExtensionPermissions(): Mos.Permissions {
      return this._acquisition.titleDefinition?.authorization?.permissions;
    }

    public getExtensionActionById(id: string): Mos.Actions {
      const runtimes: Mos.Runtimes[] = this.getExtensionRuntimes();
      for (let i: number = 0; i < runtimes.length; i++) {
        for (let j: number = 0; j < runtimes[i].actions.length; j++) {
          if (runtimes[i].actions[j].id === id) {
            return runtimes[i].actions[j];
          }
        }
      }

      return null;
    }

    public getExtensionRuntimeByActionId(id: string): Mos.Runtimes {
      const runtimes: Mos.Runtimes[] = this.getExtensionRuntimes();
      for (const runtime of runtimes) {
        for (const action of runtime.actions) {
          if (action.id === id) {
            return runtime;
          }
        }
      }

      return null;
    }

    public getStoreId(): string {
      const storeType = Mos.Acquisition.getStoreType(this._acquisition);
      return storeType == OSF.StoreType.OMEX ? this.getLocale() : storeType;
    }

    /**
     * For CD from LOB & MOS sideload, assetStoreId should be null.
     * @returns
     */
    public getAssetStoreId(): string | null {
      if (this.getSource()?.toLowerCase() === OSF.StoreType.OMEX) {
        return this.getLocale();
      } else {
        return null;
      }
    }

    public getSolutionId(): string {
      switch (Mos.Acquisition.getStoreType(this._acquisition)) {
        case OSF.StoreType.OMEX:
          return this.getAssetId();
        case OSF.StoreType.PrivateCatalog:
        case OSF.StoreType.MosExtra:
          return this.getMarketplaceID();
      }
    }

    public getIngestionId(): string {
      return this._acquisition.titleDefinition.ingestionId;
    }

    public getSource(): string {
      return this._acquisition.titleDefinition.ingestionSource;
    }

    public getState(): string {
      return this._acquisition.acquisitionState;
    }

    public getTitleId(): string {
      return this._acquisition.titleId;
    }

    public getAssetId(): string {
      return this._acquisition.assetId;
    }

    public getManifestId(): string {
      return this._acquisition.manifestId;
    }

    public getScope(): string {
      return this._acquisition.titleDefinition.scope;
    }

    public getVersion(): string {
      return this._version;
    }

    public toJSON(): any {
      return this._acquisition.toJSON();
    }

    private getWebApplicationInfo(): WebApplicationInfo {
      return this._acquisition?.titleDefinition?.webApplicationInfo;
    }

    static checkRequirements(requirements: Mos.Requirements, caller?: string): boolean {
      try {
        if (requirements?.capabilities?.length > 0) {
          // @ts-ignore TS2339
          return OSF.AppSpecificSetup.BuildRequirementsChecker()._checkSetsForMos(requirements.capabilities);
        }
      } catch (error) {
        Telemetry.RuntimeTelemetryHelper.LogExceptionTag(
          `[MosAppMetadata] checkRequirements throw exception in ${caller} with requirements capabilities: ${requirements?.capabilities}`,
          error,
          null,
          0x1e383546 /* tag_4odvg */,
        );
      }
      return true;
    }

    static isLegacyRuntime(runtime: Mos.Runtimes): boolean {
      return !(runtime.actions?.length > 0) && !(runtime.functions?.length > 0);
    }

    public containsExtension(): boolean {
      return this._acquisition?.titleDefinition?.elementDefinitions.extensions?.length > 0;
    }

    public containsOfficeAddin(): boolean {
      return this._acquisition?.titleDefinition?.elementDefinitions.officeAddIns?.length > 0;
    }

    public getNameFromTitleDefinition(): string {
      return this._acquisition?.titleDefinition?.name;
    }

    public getDescriptionFromTitleDefinition(): string {
      return this._acquisition?.titleDefinition?.shortDescription;
    }

    public getIconFromTitleDefinition(): string {
      return this._acquisition?.titleDefinition?.iconSmall?.uri;
    }

    public getProviderNameFromTitleDefinition(): string {
      return this._acquisition?.titleDefinition?.developerName;
    }
  }
}
